import React, { useEffect } from 'react';
import '../LandingPage.css';
import mbIcon from '../../assets/mb_icon.png';

interface AIERPForCBSESchoolsProps {
  onBack: () => void;
  onNavigateToLogin: () => void;
  onNavigateToContact: () => void;
}

const AIERPForCBSESchools: React.FC<AIERPForCBSESchoolsProps> = ({ onBack, onNavigateToLogin, onNavigateToContact }) => {
  useEffect(() => {
    document.title = 'AI ERP for CBSE Schools | CBSE-Compliant School Management | My Learning Board';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', 'Best AI-powered ERP software for CBSE schools. Pre-configured CCE grading, CBSE report cards, scholastic & co-scholastic areas, UDISE+ reports, and NEP 2020 compliance. Book free demo!');
    }
    return () => { document.title = 'My Learning Board – AI-Powered School Management Software & ERP in India'; };
  }, []);

  const cbseFeatures = [
    { icon: 'bi-mortarboard', title: 'CCE Pattern Grading', desc: 'Pre-configured Continuous & Comprehensive Evaluation with FA1-FA4 and SA1-SA2 assessment structure.', color: '#1565C0' },
    { icon: 'bi-file-earmark-ruled', title: 'CBSE Report Cards', desc: 'Auto-generated report cards matching CBSE format — scholastic areas, co-scholastic activities & discipline grades.', color: '#28A745' },
    { icon: 'bi-bar-chart', title: 'AI Performance Analytics', desc: 'AI tracks student progress across terms and predicts board exam performance based on internal assessments.', color: '#E53935' },
    { icon: 'bi-building', title: 'UDISE+ Integration', desc: 'One-click data export for UDISE+ compliance. AI auto-fills fields from your existing school data.', color: '#FB8C00' },
    { icon: 'bi-book', title: 'NEP 2020 Ready', desc: 'Competency-based assessments, multi-disciplinary learning tracking and 5+3+3+4 structure support.', color: '#6610F2' },
    { icon: 'bi-people', title: 'CBSE Affiliation Support', desc: 'Generate affiliation renewal reports, infrastructure data and teacher qualification reports instantly.', color: '#D81B60' },
  ];

  const cbseWorkflows = [
    {
      title: 'Term-End Assessment',
      steps: ['AI collects marks from all subject teachers', 'Auto-calculates grades using CBSE grading scale', 'Generates consolidated report cards', 'Parents notified via WhatsApp with PDF'],
    },
    {
      title: 'Board Exam Preparation',
      steps: ['AI identifies weak students using historical data', 'Recommends focus areas per student', 'Tracks improvement across mock tests', 'Generates readiness scorecard for parents'],
    },
    {
      title: 'CBSE Compliance',
      steps: ['Auto-generates UDISE+ submission data', 'Tracks teacher training hours (NISHTHA)', 'Monitors student-teacher ratio', 'AI flags compliance gaps before inspection'],
    },
  ];

  const gradingScale = [
    { grade: 'A1', range: '91-100', gp: 10 },
    { grade: 'A2', range: '81-90', gp: 9 },
    { grade: 'B1', range: '71-80', gp: 8 },
    { grade: 'B2', range: '61-70', gp: 7 },
    { grade: 'C1', range: '51-60', gp: 6 },
    { grade: 'C2', range: '41-50', gp: 5 },
    { grade: 'D', range: '33-40', gp: 4 },
    { grade: 'E', range: 'Below 33', gp: 0 },
  ];

  return (
    <div className="landing-page min-vh-100">
      {/* Navigation */}
      <nav className="navbar navbar-expand-lg navbar-light bg-white shadow-sm">
        <div className="container-fluid px-4">
          <a className="navbar-brand d-flex align-items-center" href="#" onClick={(e) => { e.preventDefault(); onBack(); }}>
            <img src={mbIcon} alt="My Learning Board" className="mb-logo" />
            <span className="ms-2 fw-bold text-primary-custom">My Learning Board</span>
          </a>
          <div className="d-flex gap-2">
            <button className="btn btn-outline-primary-custom" onClick={onBack}>
              <i className="bi bi-arrow-left me-2"></i>Home
            </button>
            <button className="btn btn-primary-custom text-white" onClick={onNavigateToLogin}>
              Login
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-5 text-white" style={{ background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)' }}>
        <div className="container-fluid px-4">
          <div className="row align-items-center py-5">
            <div className="col-lg-7">
              <span className="badge bg-white text-dark mb-3 px-3 py-2">
                <i className="bi bi-patch-check me-2"></i>CBSE Compliant
              </span>
              <h1 className="display-4 fw-bold mb-4">
                AI-Powered ERP Built for CBSE Schools
              </h1>
              <p className="lead mb-4 opacity-90">
                Pre-configured with CCE grading, CBSE report card format, UDISE+ exports,
                and NEP 2020 structure. No manual setup needed.
              </p>
              <div className="d-flex gap-3 flex-wrap">
                <button className="btn btn-warning btn-lg px-4 text-dark" onClick={onNavigateToContact}>
                  <i className="bi bi-calendar-event me-2"></i>Book Free Demo
                </button>
                <button className="btn btn-outline-light btn-lg px-4" onClick={onNavigateToLogin}>
                  <i className="bi bi-play-circle me-2"></i>Start Free Trial
                </button>
              </div>
            </div>
            <div className="col-lg-5 text-center mt-4 mt-lg-0">
              <div className="bg-white rounded-4 p-4 shadow-lg text-dark">
                <h6 className="fw-bold mb-3" style={{ color: '#1565C0' }}>CBSE Grading Scale (Built-in)</h6>
                <div className="table-responsive">
                  <table className="table table-sm mb-0" style={{ fontSize: '0.85rem' }}>
                    <thead className="table-light">
                      <tr><th>Grade</th><th>Marks</th><th>Grade Point</th></tr>
                    </thead>
                    <tbody>
                      {gradingScale.map((g, idx) => (
                        <tr key={idx}>
                          <td className="fw-semibold">{g.grade}</td>
                          <td>{g.range}</td>
                          <td>{g.gp}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CBSE Features */}
      <section className="py-5 bg-light">
        <div className="container-fluid px-4">
          <div className="text-center mb-5">
            <h2 className="fw-bold mb-3">Purpose-Built for CBSE Schools</h2>
            <p className="text-muted lead">Every feature aligns with CBSE guidelines and assessment patterns</p>
          </div>
          <div className="row g-4">
            {cbseFeatures.map((feature, idx) => (
              <div key={idx} className="col-md-6 col-lg-4">
                <div className="card h-100 border-0 shadow-sm">
                  <div className="card-body p-4">
                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                      style={{ width: '60px', height: '60px', backgroundColor: `${feature.color}15` }}>
                      <i className={`bi ${feature.icon} fs-4`} style={{ color: feature.color }}></i>
                    </div>
                    <h5 className="card-title">{feature.title}</h5>
                    <p className="card-text text-muted">{feature.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CBSE Workflows */}
      <section className="py-5">
        <div className="container-fluid px-4">
          <div className="text-center mb-5">
            <h2 className="fw-bold mb-3">AI-Powered CBSE Workflows</h2>
            <p className="text-muted lead">Automated end-to-end processes aligned with CBSE requirements</p>
          </div>
          <div className="row g-4">
            {cbseWorkflows.map((workflow, idx) => (
              <div key={idx} className="col-lg-4">
                <div className="card h-100 border-0 shadow-sm" style={{ borderTop: '4px solid #1565C0' }}>
                  <div className="card-body p-4">
                    <h5 className="fw-bold mb-3">
                      <span className="badge me-2" style={{ backgroundColor: '#1565C0' }}>{idx + 1}</span>
                      {workflow.title}
                    </h5>
                    <div className="d-flex flex-column gap-2">
                      {workflow.steps.map((step, sIdx) => (
                        <div key={sIdx} className="d-flex align-items-start">
                          <i className="bi bi-check-circle-fill me-2 mt-1" style={{ color: '#28A745', fontSize: '0.8rem' }}></i>
                          <span className="small">{step}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-5 bg-light">
        <div className="container-fluid px-4">
          <div className="row g-4 justify-content-center">
            {[
              { number: '300+', label: 'CBSE Schools', color: '#1565C0' },
              { number: '1.5L+', label: 'CBSE Students Managed', color: '#28A745' },
              { number: '100%', label: 'CCE Compliant', color: '#FFC107' },
              { number: '50%', label: 'Less Report Card Time', color: '#E53935' },
            ].map((stat, idx) => (
              <div key={idx} className="col-6 col-md-3">
                <div className="card border-0 shadow-sm text-center p-4">
                  <h2 className="fw-bold mb-1" style={{ color: stat.color }}>{stat.number}</h2>
                  <p className="text-muted small mb-0">{stat.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-5 text-white" style={{ background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)' }}>
        <div className="container-fluid px-4 text-center">
          <h2 className="mb-3">Transform Your CBSE School with AI</h2>
          <p className="lead opacity-75 mb-4">Pre-configured, CBSE-compliant, ready to go in 48 hours</p>
          <button className="btn btn-warning btn-lg px-5 text-dark" onClick={onNavigateToContact}>
            <i className="bi bi-telephone me-2"></i>Book Free Demo
          </button>
        </div>
      </section>

      <footer className="footer py-4">
        <div className="container-fluid px-4 text-center">
          <p className="mb-0 small">&copy; 2026 Omnix Digital Solutions. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default AIERPForCBSESchools;
